import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { Users, Trash2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface OrgMember {
  id: string;
  display_name: string;
  plan: string;
  role: string;
  tokens_balance: number;
  created_at: string;
}

export function MyTeam() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharedKey, setSharedKey] = useState<string | null>(null);

  useEffect(() => {
    loadMembers();
    loadMyKey();
  }, [tenant]);

  const loadMembers = async () => {
    try {
      const res = await api.listOrgMembers();
      setMembers(res.members || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMyKey = async () => {
    try {
      const res = await api.get('/api/billing/shared-key/info');
      if (res.my_key) setSharedKey(res.my_key);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRemove = async (id: string, name: string) => {
    if (!confirm(`Bạn có chắc muốn xoá nhân viên "${name}" khỏi tổ chức?\nHọ sẽ mất quyền truy cập vào các Vault và API Quota hiện tại.`)) return;
    try {
      await api.removeOrgMember(id);
      loadMembers();
    } catch (e: any) {
      alert(e.message || 'Lỗi khi xoá nhân viên');
    }
  };

  if (loading) return <div className="page-container">Loading...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">👥 My Team</h1>
          <p className="page-subtitle">Quản lý nhân viên trong tổ chức</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '15px' }}>🔑 Shared Token Key của bạn</h3>
        {sharedKey ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <code style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)' }}>
              {sharedKey}
            </code>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Gửi mã này cho nhân viên để họ nhập vào trang Billing & Usage
            </span>
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
            Bạn chưa tạo Shared Token Key. Hãy vào <b>Billing & Usage</b> để tạo mã.
          </p>
        )}
      </div>

      <div style={{ 
        background: 'var(--bg-surface)', 
        border: '1px solid var(--border)', 
        borderRadius: '8px', 
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
              <th style={{ padding: '16px 24px', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>Thành viên</th>
              <th style={{ padding: '16px 24px', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>Ngày tham gia</th>
              <th style={{ padding: '16px 24px', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Chưa có nhân viên nào trong tổ chức.
                </td>
              </tr>
            ) : (
              members.map(m => (
                <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={16} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{m.display_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>ID: {m.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>
                    {new Date(m.created_at).toLocaleDateString('vi-VN')}
                  </td>
                  <td style={{ padding: '16px 24px' }}>
                    <button 
                      className="btn btn--danger btn--sm" 
                      onClick={() => handleRemove(m.id, m.display_name)}
                      style={{ padding: '6px 12px' }}
                    >
                      <Trash2 size={14} /> Xoá
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
